/**
 * LoadingState Component
 * 
 * Displays a loading spinner while scenario data is being loaded.
 * Shows progress bar when loading avatar images (the slow part on slow connections).
 */

import { useTranslation } from 'react-i18next';

interface LoadingProgress {
  loaded: number;
  total: number;
}

interface LoadingStateProps {
  progress?: LoadingProgress | null;
}

export default function LoadingState({ progress }: LoadingStateProps) {
  const { t } = useTranslation();
  
  // Calculate percentage for progress bar
  const percentage = progress && progress.total > 0 
    ? Math.round((progress.loaded / progress.total) * 100) 
    : 0;
  
  const showProgress = progress && progress.total > 0;
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center">
      <div className="text-center text-white">
        <div className="relative mb-8">
          {/* Circular progress indicator */}
          <div className="w-24 h-24 relative mx-auto">
            {/* Background circle with pulse animation to show activity */}
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 96 96">
              {/* Pulsing outer glow to indicate activity */}
              <circle
                cx="48"
                cy="48"
                r="46"
                stroke="rgba(255,255,255,0.15)"
                strokeWidth="2"
                fill="none"
                className="animate-pulse"
              />
              {/* Background circle */}
              <circle
                cx="48"
                cy="48"
                r="42"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="8"
                fill="none"
              />
              {/* Progress circle - only show if we have progress */}
              {showProgress && (
                <circle
                  cx="48"
                  cy="48"
                  r="42"
                  stroke="white"
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 42}`}
                  strokeDashoffset={`${2 * Math.PI * 42 * (1 - percentage / 100)}`}
                  className="transition-all duration-300 ease-out"
                />
              )}
            </svg>
            {/* Center content with subtle pulse */}
            <div className="absolute inset-0 flex items-center justify-center">
              {showProgress ? (
                <span className="text-xl font-bold animate-pulse">{percentage}%</span>
              ) : (
                <span className="text-3xl animate-pulse">🎯</span>
              )}
            </div>
          </div>
          {/* Spinning dots indicator below the circle */}
          <div className="flex justify-center gap-1.5 mt-4">
            <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
        <h3 className="text-3xl font-bold mb-4">{t('scenario.session.loadingScenario')}</h3>
        <p className="text-lg opacity-90">{t('scenario.session.preparingSession')}</p>
      </div>
    </div>
  );
}

